import React, { useState, useEffect } from "react";

import {
  EnumAlertDialogType,
  EnumPopupFunctions,
  PopupInfoModel,
  ShiftModel,
} from "../Managers/dataStructures";
import { fetchAllShifts, timeout } from "../Managers/wepApiManager";
import { ActivityIndicator, Toast } from "../Managers/utilityComponents";
import { getLanguage } from "../Managers/dataProvider";

interface ShiftsModelProps {
  array_shifts: ShiftModel[];
  openShiftDetail: (item: PopupInfoModel) => void;
}

export default function Shifts(props: ShiftsModelProps) {
  const isEnglish = getLanguage();
  const [array_shifts, setArray_Shifts] = useState<ShiftModel[]>(
    props.array_shifts
  );
  const [isActivityRunning, setIsActivityRunning] = useState<boolean>(false);
  const [toastText, setToastText] = useState<string>("");
  const [isToastVisible, setIsToastVisible] = useState<EnumAlertDialogType>(
    EnumAlertDialogType.NEUTRAL
  );

  useEffect(() => {
    setIsActivityRunning(true);
    fetchAllShifts((x) => {
      x.sort((a, b) => {
        return a.title.toLocaleLowerCase() > b.title.toLocaleLowerCase()
          ? 1
          : -1;
      });
      setArray_Shifts(x);
      setIsActivityRunning(false);
    });
  }, []);

  function openShiftModel(shift_: ShiftModel) {
    const item_: PopupInfoModel = {
      item: shift_,
      ftype: EnumPopupFunctions.EDIT_SHIFT,
      callback: (_item: ShiftModel) => {
        showAlert(
          true,
          isEnglish
            ? "Shift successfully updated."
            : "Schicht erfolgreich aktualisiert.",
          () => {}
        );
      },
    };
    props.openShiftDetail(item_);
  }

  function addShift() {
    const item_: PopupInfoModel = {
      item: null,
      ftype: EnumPopupFunctions.ADD_SHIFT,
      callback: (item: ShiftModel) => {
        let tempArray = array_shifts;
        tempArray.push(item);
        setArray_Shifts(tempArray);
        showAlert(
          true,
          isEnglish
            ? "Shift successfully added"
            : "Schicht erfolgreich hinzugefügt.",
          () => {}
        );
      },
    };
    props.openShiftDetail(item_);
  }

  async function showAlert(
    isPositive: boolean,
    text: string,
    callback: () => void
  ) {
    setToastText(text);
    if (isPositive) {
      setIsToastVisible(EnumAlertDialogType.POSITIVE);
    } else {
      setIsToastVisible(EnumAlertDialogType.NEGATIVE);
    }
    await timeout(3000);
    setIsToastVisible(EnumAlertDialogType.NEUTRAL);
    callback();
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-base font-semibold leading-6 text-gray-900">
            {isEnglish ? "Shifts" : "Schichten"}
          </h1>
          <p className="mt-2 text-sm text-gray-700">
            {isEnglish
              ? "A list of all Shifts along with their start and end time."
              : "Sie können die Arbeitszeiten für die einzelnen Filialen festlegen. Diese Zeiten werden automatisch bei der Schichtplanung berücksichtigt "}
          </p>
        </div>
        <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
          <button
            onClick={addShift}
            type="button"
            className="block rounded-md bg-indigo-600 px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
          >
            {isEnglish ? "Add Shift" : "Arbeitszeiten hinzufügen"}
          </button>
        </div>
      </div>
      <div className="mt-8 flow-root">
        {isToastVisible !== EnumAlertDialogType.NEUTRAL
          ? Toast(toastText, isToastVisible)
          : null}
        <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
            <div className="relative">
              {isActivityRunning ? (
                <div className="min-w-full h-20 flex flex-row items-center justify-center">
                  {ActivityIndicator(1)}
                </div>
              ) : (
                <table className="min-w-full table-fixed divide-y divide-gray-300">
                  <thead>
                    <tr>
                      <th
                        scope="col"
                        className="min-w-[12rem] py-3.5 pr-3 text-left text-sm font-semibold text-gray-900"
                      >
                        {isEnglish ? "Shift Title" : "Schichtname"}
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >
                        {isEnglish ? "Start Time" : "Arbeitsbeginn"}
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >
                        {isEnglish ? "End Time" : "Arbeitsende"}
                      </th>

                      <th
                        scope="col"
                        className="relative py-3.5 pl-3 pr-4 sm:pr-3"
                      >
                        <span className="sr-only">
                          {isEnglish ? "Edit" : "Bearbeiten"}
                        </span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {/* Selected: "bg-gray-50" */}
                    {array_shifts.map((x) => [CellShifts(x, openShiftModel)])}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function CellShifts(shift: ShiftModel, handler: (report: ShiftModel) => void) {
  const isEnglish = getLanguage();
  function onEdit() {
    handler(shift);
  }

  return (
    <tr key={shift.shift_id}>
      <td className="whitespace-nowrap py-4 pr-3 text-sm font-medium text-gray-900">
        {shift.title}
      </td>
      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
        {"" + shift.start_time}
      </td>
      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
        {"" + shift.end_time}
      </td>
      <td className="whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-3">
        <a
          onClick={onEdit}
          href={"#" + shift.shift_id}
          className="text-indigo-600 hover:text-indigo-900"
        >
          {isEnglish ? "Edit" : "Bearbeiten"}
          <span className="sr-only">{", " + shift.title}</span>
        </a>
      </td>
    </tr>
  );
}
